import React, { useState, useEffect } from 'react';
import { Button, Tooltip, Dropdown, Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';

import {
  SaveIcon,
  DownloadIcon,
  SavedIcon,
  SavingIcon,
  PreviewIcon,
  KeyboardButton,
  TocIcon,
  SettingsGearIcon,
  MoreSqaureIcon,
  HistoryIcon,
  CloseIcon,
} from 'components/Icons';
import { SettingsModal, IntegrationModal } from './';
import { useProposalContext } from 'context/proposal';
import { scrollToSection } from 'helpers/proposal';

const DraftHeader = ({
  openPublishView,
  user,
  isSection,
  handleManualSave,
  disablePreview,
  configText,
  path,
  setKeyboardModal,
  setCollapseContentTable,
  setCollapseSidemenu,
  openHistory,
}) => {
  const [showShortcut, setShowShortcut] = useState(false);
  const [collapseIconsMenu, setCollapseIconsMenu] = useState(true);

  const {
    downloadPdf,
    downloadingPDF,
    handleDownloadCancel,
    setProp,
    propRef,
    proposal,
    savingDraftProposal: isSaving,
    saveTemplateProposal: saveProposal,
    updateLoaderState,
  } = useProposalContext();

  useEffect(() => {
    // open the keyboard shortcut modal on ctrl + shift + s
    const handleKeyPress = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.keyCode === 83) {
        e.preventDefault();
        setKeyboardModal(true);
      }
    };

    window.addEventListener('keydown', handleKeyPress);

    // for ipad and smaller screen hide
    const isiPad = /iPad/i.test(navigator.userAgent);
    setShowShortcut(!isiPad && window.innerWidth > 700 && !!setKeyboardModal);

    return () => {
      window.removeEventListener('keydown', handleKeyPress);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const versionHistory = !isSection && (
    <Tooltip placement="bottom" title="Version History" color="white">
      <span>
        <Button
          className="medium-btn transparent-btn option-btn"
          icon={<HistoryIcon />}
          onClick={openHistory}
          disabled={isSaving}
          aria-hidden="true">
          <span className="below-lg">Version History</span>
        </Button>
      </span>
    </Tooltip>
  );

  const settingsMenu = (
    <div>
      {proposal && !isSection && (
        <IntegrationModal
          user={user}
          proposal={proposal}
          saveProposal={saveProposal}
          isSaving={isSaving}
          setCollapseIconsMenu={setCollapseIconsMenu}
        />
      )}

      {proposal && !isSection && (
        <SettingsModal
          user={user}
          proposal={proposal}
          saveProposal={saveProposal}
          updateLoaderState={updateLoaderState}
          scrollToSection={scrollToSection}
          configText={configText}
          propRef={propRef}
          setProp={setProp}
          path={path}
          setCollapseIconsMenu={setCollapseIconsMenu}
        />
      )}

      {versionHistory}

      {!isSection && (
        <Button
          className="medium-btn transparent-btn option-btn"
          icon={<DownloadIcon />}
          onClick={downloadPdf}
          aria-hidden="true">
          Download PDF
        </Button>
      )}
    </div>
  );

  return (
    <div className="main-wrapper draft">
      <div className="block block-1">
        <div>
          {showShortcut && (
            <Button
              className="medium-btn transparent-btn option-btn above-md"
              icon={<KeyboardButton />}
              onClick={() => setKeyboardModal(true)}></Button>
          )}
        </div>
        <div className="btn-wrapper editor-save-block">
          <Tooltip title={isSaving ? 'Saving' : 'Saved'}>
            {isSaving ? (
              <SavingIcon className="saving-icon" />
            ) : (
              <SavedIcon className="saved-icon" />
            )}
          </Tooltip>

          <Tooltip title="Save" className="save-proposal above-md" onClick={handleManualSave}>
            <SaveIcon />
          </Tooltip>
        </div>
      </div>

      {/* for smaller screen */}
      <div className="block block-2 icon-block below-lg">
        <Button
          className="large-btn grey-btn icon-btn toc-btn"
          onClick={() => {
            setCollapseIconsMenu(true);
            setCollapseSidemenu(true);
            setCollapseContentTable((prev) => !prev);
          }}>
          <TocIcon />
        </Button>

        <Dropdown
          overlay={settingsMenu}
          placement="bottomRight"
          overlayClassName="header-settings-dropdown below-lg"
          visible={!collapseIconsMenu}
          onVisibleChange={(visible) => {
            setCollapseIconsMenu(!visible);
            if (visible) {
              setCollapseContentTable(true);
              setCollapseSidemenu(true);
            }
          }}
          trigger="click">
          <Button
            className={`large-btn grey-btn icon-btn settings-btn ${
              !collapseIconsMenu ? 'active' : ''
            }`}
            onClick={() => {
              setCollapseIconsMenu((prev) => !prev);
            }}>
            <SettingsGearIcon />
          </Button>
        </Dropdown>

        <Button
          className="large-btn grey-btn icon-btn sidemenu-btn"
          onClick={() => {
            setCollapseContentTable(true);
            setCollapseIconsMenu(true);
            setCollapseSidemenu((prev) => !prev);
          }}>
          <MoreSqaureIcon />
        </Button>
      </div>

      {/* for bigger screen */}
      <div className="block block-2 option-block above-lg">
        {versionHistory}

        {proposal && !isSection && (
          <IntegrationModal
            user={user}
            proposal={proposal}
            saveProposal={saveProposal}
            isSaving={isSaving}
          />
        )}

        {!isSection && (
          <Tooltip
            placement="bottom"
            title={downloadingPDF ? 'Downloading PDF' : 'Download PDF'}
            color="white">
            <Button
              className={`medium-btn transparent-btn option-btn ${downloadingPDF ? 'loading' : ''}`}
              icon={<DownloadIcon className="main-icon" />}
              onClick={downloadPdf}
              aria-hidden="true">
              {downloadingPDF && (
                <>
                  <Spin indicator={<LoadingOutlined spin />} size="large" />
                  <CloseIcon className="close-icon" onClick={handleDownloadCancel} />
                </>
              )}
            </Button>
          </Tooltip>
        )}

        {proposal && !isSection && (
          <SettingsModal
            user={user}
            proposal={proposal}
            saveProposal={saveProposal}
            updateLoaderState={updateLoaderState}
            scrollToSection={scrollToSection}
            configText={configText}
            propRef={propRef}
            setProp={setProp}
            path={path}
          />
        )}
      </div>

      <div className="block block-last">
        <Button
          className="preview-button medium-btn secondary-btn"
          onClick={() => {
            if (disablePreview !== 'saving-layout' && openPublishView) {
              openPublishView();
            }
          }}
          disabled={isSaving}
          // loading={true}>
          loading={isSaving}>
          <span>
            <span>Preview</span>
            <span className="above-md">&nbsp;& Save</span>
          </span>
          <PreviewIcon className="above-md send-icon" />
        </Button>
      </div>
    </div>
  );
};

DraftHeader.defaultProps = {
  openPublishView: () => {},
  user: '',
  isSection: false,
  handleManualSave: () => {},
  disablePreview: false,
  configText: () => {},
  path: '',
  setKeyboardModal: () => {},
  setCollapseContentTable: () => {},
  setCollapseSidemenu: () => {},
  openHistory: () => {},
};

DraftHeader.propTypes = {
  openPublishView: PropTypes.func,
  user: PropTypes.oneOfType([PropTypes.instanceOf(Object), PropTypes.string]),
  isSection: PropTypes.bool,
  handleManualSave: PropTypes.func,
  disablePreview: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  configText: PropTypes.func,
  path: PropTypes.string,
  setKeyboardModal: PropTypes.func,
  setCollapseContentTable: PropTypes.func,
  setCollapseSidemenu: PropTypes.func,
  openHistory: PropTypes.func,
};
export default DraftHeader;
